import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { clearTokens, decodeIdToken, loadTokens, logout as endSession } from './lib/auth'
import { config } from './lib/config'

/**
 * Who is signed in, from the client's point of view.
 *
 * Deliberately thin: it reports what the stored id token claims and nothing more.
 * Authorization is the API's job -- it re-derives the subject from the verified
 * JWT on every request -- so nothing here is ever a permission check.
 */

export interface Session {
  userId: string
  email?: string
  displayName?: string
}

interface SessionContextValue {
  session?: Session
  /** Forget the local session and end the Cognito one. */
  signOut: () => void
  /** Re-read the token store, after a login completes in this tab. */
  refresh: () => void
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined)

function readSession(): Session | undefined {
  if (import.meta.env.DEV && config.devUser) {
    return {
      userId: config.devUser.userId,
      email: config.devUser.email,
      displayName: config.devUser.displayName,
    }
  }
  const tokens = loadTokens()
  if (!tokens) return undefined
  const claims = decodeIdToken(tokens.idToken)
  if (!claims?.sub) {
    // An id token we cannot read is worse than none: every request would 401.
    clearTokens()
    return undefined
  }
  return {
    userId: claims.sub,
    ...(claims.email ? { email: claims.email } : {}),
    ...(claims.name ? { displayName: claims.name } : {}),
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | undefined>(readSession)

  const refresh = useCallback(() => setSession(readSession()), [])
  const signOut = useCallback(() => {
    setSession(undefined)
    endSession()
  }, [])

  const value = useMemo(
    () => ({ ...(session ? { session } : {}), signOut, refresh }),
    [session, signOut, refresh],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession must be used inside a SessionProvider')
  return ctx
}
